import Dexie, { type EntityTable } from 'dexie';

export type SyncOperation = 'create' | 'update' | 'delete';

/** Key-value setting persisted locally for device identity and config. */
export interface LocalSetting {
  key: string;      // PK — unique
  value: string;
  updated_at: number; // Date.now()
}

export interface SyncQueueItem {
  id: string;
  module: 'timelog' | 'employee' | 'access_role' | 'face_embedding_v2' | 'card_enrollment' | 'pin_enrollment';
  operation: SyncOperation;
  endpoint: string;
  payload: unknown;
  createdAt: number;
  attempts: number;
  lastAttemptAt: number | null;
  lastError: string | null;
  status: 'pending' | 'in_progress' | 'failed_permanent';
}

/** Empleado almacenado localmente (offline-first). */
export interface LocalEmployee {
  idPerson: number;       // PK
  idAccessRole: number;   // index para consultas por perfil
  syncStatus: 'synced' | 'pending';
  updatedAt: number;      // Date.now() del último sync
  raw: unknown;           // EmployeeDTO serializado
}

/** Marcaje almacenado localmente antes de sincronizar. */
export interface LocalTimelog {
  localId: string;        // PK — uuid generado en cliente
  idPerson: number;       // index
  dateTimeEvent: string;  // ISO string, index para ordenar
  syncStatus: 'synced' | 'pending' | 'failed';
  serverId: number | null;
  raw: unknown;           // payload del timelog
}

/** Perfil de acceso (access role) cacheado localmente. */
export interface LocalAccessRole {
  idAccessRole: number;   // PK
  raw: unknown;           // AccessRoleDTO serializado
}

/** Remote timelog cached from API (offline-first query). */
export interface RemoteTimelog {
  idTimelog: number;       // PK — server-side timelog ID
  idAccessRole: number;    // index for profile-based queries
  idPerson: number;        // index for person-based queries
  dateTimeEvent: string;   // ISO string, index for range queries
  raw: unknown;            // serialized Timelog entity
}

/** Cached range metadata for incremental fetching. */
export interface CachedRange {
  idAccessRole: number;    // PK
  oldestCached: string;    // ISO string
  newestCached: string;    // ISO string
  lastFetchedAt: number;   // Date.now() timestamp
}

/**
 * Face embedding 512-d MobileFaceNet/ArcFace.
 * Only stores v2 (512-d) — legacy 128-d FaceNet embeddings are never persisted here.
 * compound key: [idPerson, idAccessRole]
 */
export interface LocalFaceEmbedding {
  idPerson: number;
  idAccessRole: number;
  embedding: number[];       // always 512-d
  dim: 512;
  source: 'remote-v2' | 'local-enrolled';
  syncStatus: 'synced' | 'pending';
  updatedAt: number;
}

/**
 * Cached photo blob for offline-first avatar rendering.
 * Added in v6 — additive migration, no existing records touched.
 *
 * `blob:` object URLs produced from these blobs bypass the Service Worker
 * interceptor entirely (blob: is always same-origin), making this an L1 cache
 * that is deterministic, testable, and independent of SW quota/eviction rules.
 */
export interface PhotoBlobRecord {
  url: string;        // PK — canonical remote URL (e.g. "https://api.../file/abc.jpg")
  blob: Blob;         // raw image bytes
  fetchedAt: number;  // Date.now() at fetch time — available for optional TTL eviction
}

export class AppDatabase extends Dexie {
  employees!:      EntityTable<LocalEmployee,    'idPerson'>;
  timelogs!:       EntityTable<LocalTimelog,     'localId'>;
  accessRoles!:    EntityTable<LocalAccessRole,  'idAccessRole'>;
  syncQueue!:      EntityTable<SyncQueueItem,    'id'>;
  // Compound key is handled as an array tuple; Dexie's EntityTable type requires a string key.
  // We use 'idPerson' as the TS nominal key — actual DB key is the compound [idPerson+idAccessRole].
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  faceEmbeddings!: EntityTable<LocalFaceEmbedding, any>;
  settings!:       EntityTable<LocalSetting,     'key'>;
  remoteTimelogs!: EntityTable<RemoteTimelog,    'idTimelog'>;
  cachedRanges!:   EntityTable<CachedRange,      'idAccessRole'>;
  photoBlobs!:     EntityTable<PhotoBlobRecord,  'url'>;

  constructor() {
    super('myintelli-clock');
    this.version(1).stores({
      employees:   'idPerson, idAccessRole, syncStatus',
      timelogs:    'localId, idPerson, dateTimeEvent, syncStatus',
      accessRoles: 'idAccessRole',
      syncQueue:   'id, status, createdAt, [module+status]',
    });
    // v2: adds faceEmbeddings table (does not destroy existing data)
    this.version(2).stores({
      employees:      'idPerson, idAccessRole, syncStatus',
      timelogs:       'localId, idPerson, dateTimeEvent, syncStatus',
      accessRoles:    'idAccessRole',
      syncQueue:      'id, status, createdAt, [module+status]',
      faceEmbeddings: '[idPerson+idAccessRole], idAccessRole, syncStatus, source, updatedAt',
    });
    // v3: adds settings table for device identity (key-value, offline-first)
    // All existing tables are preserved — additive migration only.
    this.version(3).stores({
      employees:      'idPerson, idAccessRole, syncStatus',
      timelogs:       'localId, idPerson, dateTimeEvent, syncStatus',
      accessRoles:    'idAccessRole',
      syncQueue:      'id, status, createdAt, [module+status]',
      faceEmbeddings: '[idPerson+idAccessRole], idAccessRole, syncStatus, source, updatedAt',
      settings:       '&key, updated_at',
    });
    // v4: adds remoteTimelogs + cachedRanges tables for offline-first timelog sync
    // All existing tables are preserved — additive migration only.
    this.version(4).stores({
      employees:       'idPerson, idAccessRole, syncStatus',
      timelogs:        'localId, idPerson, dateTimeEvent, syncStatus',
      accessRoles:     'idAccessRole',
      syncQueue:       'id, status, createdAt, [module+status]',
      faceEmbeddings:  '[idPerson+idAccessRole], idAccessRole, syncStatus, source, updatedAt',
      settings:        '&key, updated_at',
      remoteTimelogs:  '&idTimelog, idAccessRole, idPerson, dateTimeEvent, [idAccessRole+dateTimeEvent]',
      cachedRanges:    '&idAccessRole',
    });
    // v5: migrate legacy local verificationMode IDs in timelogs drafts to backend canonical IDs.
    //   1 (old local facial) → 11 (CLOCK_WEB_MODE.facial / FACIAL_PC)
    //   3 (old local card)   → 2  (CLOCK_WEB_MODE.card  / CARD)
    //
    // Conservative: vMode=2 is NOT migrated — ambiguous (could be new-canonical CARD already,
    // or old local PIN awaiting a different resolution). PIN drafts are rare edge cases;
    // vMode=13 (PIN_PC) is the canonical value going forward for all newly-created drafts.
    //
    // Idempotent: canonical IDs (11, 13, 2) have no entry in MAP and are left unchanged.
    // Schema is unchanged from v4 — only data migration.
    this.version(5).stores({
      employees:       'idPerson, idAccessRole, syncStatus',
      timelogs:        'localId, idPerson, dateTimeEvent, syncStatus',
      accessRoles:     'idAccessRole',
      syncQueue:       'id, status, createdAt, [module+status]',
      faceEmbeddings:  '[idPerson+idAccessRole], idAccessRole, syncStatus, source, updatedAt',
      settings:        '&key, updated_at',
      remoteTimelogs:  '&idTimelog, idAccessRole, idPerson, dateTimeEvent, [idAccessRole+dateTimeEvent]',
      cachedRanges:    '&idAccessRole',
    }).upgrade((tx) => {
      const MAP: Record<number, number> = { 1: 11, 3: 2 };
      return tx.table('timelogs').toCollection().modify((rec: { raw?: { verificationMode?: number } }) => {
        const oldMode = rec.raw?.verificationMode;
        if (oldMode !== undefined) {
          const newMode = MAP[oldMode];
          if (newMode !== undefined) {
            rec.raw = { ...rec.raw, verificationMode: newMode };
          }
        }
      });
    });
    // v6: adds photoBlobs table for offline-first avatar photo cache (L1 blob store).
    // Additive migration only — no existing store is dropped, renamed, or restructured.
    // `&url` = unique primary key (canonical remote URL).
    // `fetchedAt` index is available for optional future TTL eviction.
    this.version(6).stores({
      employees:       'idPerson, idAccessRole, syncStatus',
      timelogs:        'localId, idPerson, dateTimeEvent, syncStatus',
      accessRoles:     'idAccessRole',
      syncQueue:       'id, status, createdAt, [module+status]',
      faceEmbeddings:  '[idPerson+idAccessRole], idAccessRole, syncStatus, source, updatedAt',
      settings:        '&key, updated_at',
      remoteTimelogs:  '&idTimelog, idAccessRole, idPerson, dateTimeEvent, [idAccessRole+dateTimeEvent]',
      cachedRanges:    '&idAccessRole',
      photoBlobs:      '&url, fetchedAt',
    });
  }
}

export const db = new AppDatabase();
